import type { UploadAdapter, FileLoader } from '@ckeditor/ckeditor5-upload/src/filerepository'

// HACK: Temporary log. If this has more than a few months, please delete it or do it properly by passing
//  the logger from outside. Our editor plugins should have minimal "owl-nest"-y dependencies.
import * as logger from '@owl-nest/logger'

type UploadOptions = {
  abortSignal?: AbortSignal
}

export type Uploaded = {
  default: string
} & { [s: string]: string }

export type UluleUploadAdapterOptions = {
  upload: (file: File, options: UploadOptions) => Promise<Uploaded>
  onUploadSuccess?: () => void
  onUploadFailure?: (error: unknown) => void
}

export class UluleUploadAdapter implements UploadAdapter {
  private loader: FileLoader
  private options: UluleUploadAdapterOptions
  private abortController?: AbortController

  constructor(loader: FileLoader, options: UluleUploadAdapterOptions) {
    this.loader = loader
    this.options = options
  }

  private async _realUpload(): Promise<Uploaded> {
    if (this.loader.file === null) {
      throw Error('loader is null')
    }

    const file = await this.loader.file

    if (file === null) {
      throw Error('file is null')
    }

    this.abortController = new AbortController()

    // TODO: Log this, to know if there was abortion
    return this.options.upload(file, {
      abortSignal: this.abortController.signal,
    })
  }

  // TODO: Make `retry` actually optional by moving it elsewhere, we don't have full control over `upload()` calls
  async upload(retry = 3): Promise<Uploaded> {
    try {
      const uploaded = await this._realUpload()

      if (this.options.onUploadSuccess) {
        this.options.onUploadSuccess()
      }

      return uploaded
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 422) {
        if (this.options.onUploadFailure) {
          this.options.onUploadFailure(error)
        }
        return Promise.reject(error)
      }

      if (retry > 0) {
        if (retry === 3) {
          // logger.warn('[editor] Retrying upload')
        }
        await new Promise((r) => setTimeout(r, 2000))
        const reuploadResponse = await this.upload(retry - 1)
        if (reuploadResponse) {
          return reuploadResponse
        }
      }

      const healthCheck = await fetch(`https://api.ulule.com/healthcheck`)
      logger.err('[editor] Ulule upload adapter failed to upload image', {
        error,
        healthCheck,
      })

      if (this.options.onUploadFailure) {
        this.options.onUploadFailure(error)
      }

      return Promise.reject(error)
    }
  }

  abort(): void {
    if (this.abortController) {
      this.abortController.abort()
    }
  }
}
